import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, PlusCircle, History, BarChart3, User, Users, Shield } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { TrialBanner } from "./TrialBanner";

const ICON = "/mytrack-icon.png";

const NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true, testid: "nav-home" },
  { to: "/add", label: "Log Run", icon: PlusCircle, testid: "nav-add" },
  { to: "/history", label: "History", icon: History, testid: "nav-history" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/pit-crew", label: "Pit Crew", icon: Users, testid: "nav-pitcrew" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export function Layout({ children }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const items = [...NAV];
  if (user?.role === "admin") items.push({ to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" });
  return (
    <div className="min-h-screen bg-mt-bg">
      {/* Desktop top nav */}
      <header className="sticky top-0 z-50 hidden border-b border-mt-border bg-mt-bg/80 backdrop-blur-xl md:block">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-6 py-3">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="brand-logo">
            <img src={ICON} alt="MyTrack" className="h-9 w-9 rounded-lg" />
            <span className="font-head text-lg font-black tracking-tight">MyTrack<span className="text-mt-primary">App</span></span>
          </button>
          <nav className="ml-4 flex items-center gap-1">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`${n.testid}-desktop`}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "bg-mt-primary text-white" : "text-mt-text hover:bg-mt-secondary"
                  }`
                }
              >
                <n.icon size={16} /> {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-50 flex items-center justify-between border-b border-mt-border bg-mt-bg/80 px-4 py-3 backdrop-blur-xl md:hidden">
        <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="brand-logo-mobile">
          <img src={ICON} alt="MyTrack" className="h-8 w-8 rounded-lg" />
          <span className="font-head text-base font-black tracking-tight">MyTrack<span className="text-mt-primary">App</span></span>
        </button>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-6xl px-4 pb-28 pt-4 md:px-6 md:pb-10 md:pt-6"
      >
        <TrialBanner />
        {children}
      </motion.main>

      {/* Mobile bottom tab bar */}
      <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-mt-border bg-mt-card/95 backdrop-blur-xl md:hidden">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold ${
                  isActive ? "text-mt-primary" : "text-mt-mutedfg"
                }`
              }
            >
              <n.icon size={20} />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
